/**
 * Wrapped — "Bodenblick Wrapped" year-in-review page.
 *
 * Route: /wrapped/:year (defaults to current year if missing)
 *
 * Scroll-driven highlight reel built from /api/wrapped/{year}.
 * Each section is a full-viewport scene with bold typography,
 * staggered entrance animations and an instagram-friendly final
 * share frame.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowLeft, Leaf, Newspaper, Sparkle, Calendar, Trophy, ShareNetwork, DownloadSimple } from "@phosphor-icons/react";

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

const MONTH_LABELS = ["Jan", "Feb", "Mär", "Apr", "Mai", "Jun", "Jul", "Aug", "Sep", "Okt", "Nov", "Dez"];

export default function Wrapped() {
  const params = useParams();
  const year = parseInt(params.year || `${new Date().getFullYear()}`, 10);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios.get(`${API}/api/wrapped/${year}`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "Daten nicht verfügbar"));
  }, [year]);

  const { scrollYProgress } = useScroll();
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "-30%"]);

  const monthMax = useMemo(
    () => Math.max(1, ...(data?.months_dist || []).map((m) => m.count || 0)),
    [data]
  );

  if (error) {
    return (
      <ErrorWrap message={error} year={year} />
    );
  }
  if (!data) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center" data-testid="wrapped-loading">
        <div className="animate-pulse text-sm font-mono opacity-60">Bodenblick Wrapped · {year} · lädt…</div>
      </div>
    );
  }

  const noData = data.total_posts === 0;

  return (
    <>
      <Helmet>
        <title>{`Bodenblick Wrapped ${year}`}</title>
        <meta name="description" content={`Bodenblick Wrapped ${year} — ${data.total_posts} Beiträge, ${data.top_topics?.[0]?.label || "viele Themen"}.`} />
      </Helmet>
      <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="wrapped-page">
        {/* Sticky back-link */}
        <header className="sticky top-0 z-30 backdrop-blur-md bg-white/10 border-b border-white/10">
          <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
            <Link to="/" className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest opacity-80 hover:opacity-100" data-testid="wrapped-back">
              <ArrowLeft size={12} /> <Leaf size={12} weight="fill" /> Bodenblick
            </Link>
            <span className="text-[10px] font-mono tracking-widest opacity-50">WRAPPED · {year}</span>
          </div>
        </header>

        {/* Scene 1 — Hero */}
        <Scene>
          <motion.div style={{ y: bgY }} className="absolute inset-0 opacity-20 pointer-events-none">
            <div className="absolute -top-20 -left-20 w-96 h-96 rounded-full blur-3xl" style={{ background: "radial-gradient(circle, #FFC24B, transparent 60%)" }} />
            <div className="absolute bottom-0 -right-20 w-96 h-96 rounded-full blur-3xl" style={{ background: "radial-gradient(circle, rgba(0,250,154,0.35), transparent 60%)" }} />
          </motion.div>
          <div className="relative text-center max-w-3xl mx-auto px-4">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-[11px] uppercase tracking-[0.4em] font-mono text-[#FFC24B]"
            >
              Bodenblick · Jahresrückblick
            </motion.p>
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="font-display text-7xl sm:text-8xl lg:text-[10rem] font-bold tracking-tighter leading-[0.88] mt-4"
            >
              {year}
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="mt-8 text-xl sm:text-2xl text-white/70 leading-snug"
            >
              {noData
                ? `${year} ist noch leer — Bodenblick beobachtet ab jetzt.`
                : `${data.total_posts} Beiträge. ${data.science_count} davon mit wissenschaftlicher Quelle. Eine Reise durch das Jahr.`}
            </motion.p>
          </div>
        </Scene>

        {noData ? null : (
          <>
            {/* Scene 2 — Top topics */}
            <Scene bg="#00FA9A">
              <div className="max-w-4xl mx-auto px-4 w-full">
                <SceneLabel>Deine Lieblings-Themen</SceneLabel>
                <h2 className="font-display text-5xl sm:text-7xl font-bold leading-[0.95] tracking-tighter mt-4">
                  Worüber wir am meisten geschrieben haben.
                </h2>
                <div className="mt-12 space-y-6" data-testid="wrapped-top-topics">
                  {(data.top_topics || []).map((t, i) => (
                    <motion.div
                      key={t.topic}
                      initial={{ opacity: 0, x: -30 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5, delay: i * 0.12 }}
                      className="border-l-4 border-[#FFC24B]/40 pl-6"
                    >
                      <div className="flex items-baseline gap-4">
                        <span className="font-mono text-2xl opacity-50">#{i + 1}</span>
                        <span className="text-3xl sm:text-4xl font-bold">{t.label}</span>
                        <span className="ml-auto text-base font-mono opacity-60">{t.count} Beiträge · {t.share}%</span>
                      </div>
                      <div className="mt-2 h-1.5 bg-white/15 overflow-hidden">
                        <motion.div
                          initial={{ width: 0 }}
                          whileInView={{ width: `${t.share}%` }}
                          viewport={{ once: true }}
                          transition={{ duration: 1, delay: i * 0.12 + 0.2, ease: "easeOut" }}
                          className="h-full bg-[#FFC24B]"
                        />
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </Scene>

            {/* Scene 3 — Monthly chart */}
            <Scene bg="#D9A87C">
              <div className="max-w-4xl mx-auto px-4 w-full">
                <SceneLabel>Dein Jahr in Wellen</SceneLabel>
                <h2 className="font-display text-5xl sm:text-7xl font-bold leading-[0.95] tracking-tighter mt-4">
                  Wann war was los?
                </h2>
                <div className="mt-12 grid grid-cols-12 gap-1 h-48 sm:h-64 items-end" data-testid="wrapped-months">
                  {(data.months_dist || []).map((m, i) => (
                    <motion.div
                      key={m.month}
                      initial={{ height: 0 }}
                      whileInView={{ height: `${(m.count / monthMax) * 100}%` }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5, delay: i * 0.04 }}
                      className="bg-[#FFC24B] relative group"
                      style={{ minHeight: 4 }}
                    >
                      <span className="absolute -bottom-6 left-1/2 -translate-x-1/2 text-[10px] font-mono opacity-70">
                        {MONTH_LABELS[i]}
                      </span>
                      <span className="absolute -top-5 left-1/2 -translate-x-1/2 text-[10px] font-mono opacity-90 opacity-0 group-hover:opacity-100 transition">
                        {m.count}
                      </span>
                    </motion.div>
                  ))}
                </div>
                <p className="mt-12 text-base opacity-70 leading-relaxed">
                  Höchste Aktivität: <strong>{MONTH_LABELS[(data.months_dist || []).reduce((p, c) => c.count > p.count ? c : p, { count: 0, month: 1 }).month - 1]}</strong>.
                </p>
              </div>
            </Scene>

            {/* Scene 4 — Highlights */}
            <Scene bg="#1A1A1A">
              <div className="max-w-5xl mx-auto px-4 w-full">
                <SceneLabel>Highlight-Beiträge</SceneLabel>
                <h2 className="font-display text-5xl sm:text-7xl font-bold leading-[0.95] tracking-tighter mt-4">
                  Fünf, die berührt haben.
                </h2>
                <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="wrapped-highlights">
                  {(data.highlight_posts || []).map((p, i) => (
                    <motion.a
                      key={p.id}
                      href={`/heute/${p.date_iso}#${p.id}`}
                      initial={{ opacity: 0, y: 30 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5, delay: i * 0.1 }}
                      className="block bg-[#121212] text-white border border-white/20 hover:border-[#FFC24B]/40 transition-colors group"
                      data-testid={`wrapped-highlight-${i}`}
                    >
                      {p.image_url && (
                        <div className="aspect-square overflow-hidden bg-white/10">
                          <img src={`${API}${p.image_url}`} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
                        </div>
                      )}
                      <div className="p-4">
                        <div className="text-[10px] font-mono uppercase tracking-widest text-[#00FA9A]">
                          {p.topic_label || "Bodenblick"} · {(p.date_iso || "").slice(5)}
                        </div>
                        <h3 className="font-bold text-base leading-tight mt-1.5 line-clamp-2">{p.title}</h3>
                        <p className="text-[12px] text-white/70 leading-snug mt-1 line-clamp-3">{p.teaser}</p>
                      </div>
                    </motion.a>
                  ))}
                </div>
              </div>
            </Scene>
          </>
        )}

        {/* Final scene — share card */}
        <Scene bg="#FFC24B">
          <div className="max-w-3xl mx-auto px-4 text-center">
            <Trophy size={48} weight="duotone" className="mx-auto text-white" />
            <h2 className="font-display text-5xl sm:text-7xl font-bold text-white mt-6 tracking-tighter leading-[0.95]">
              Auf {year + 1}.
            </h2>
            <p className="mt-6 text-lg text-white/80 leading-relaxed">
              Danke, dass du Bodenblick begleitest. Wir blicken weiter — auf den
              Boden, das Wetter, die Bestäuber.
            </p>
            <div className="mt-10 flex items-center justify-center gap-3 flex-wrap">
              <Link
                to="/heute"
                className="inline-flex items-center gap-2 bg-[#F5F5F5] text-[#0A0A0A] px-5 py-3 text-sm font-medium uppercase tracking-widest hover:bg-[#00FA9A] transition-colors"
                data-testid="wrapped-cta-heute"
              >
                <Newspaper size={14} /> Heute lesen
              </Link>
              <a
                href={`${API}/api/wrapped/${year}/poster.png`}
                download={`bodenblick-wrapped-${year}.png`}
                className="inline-flex items-center gap-2 bg-[#F5F5F5] text-[#0A0A0A] px-5 py-3 text-sm font-medium uppercase tracking-widest hover:bg-[#00FA9A] transition-colors"
                data-testid="wrapped-download-poster"
              >
                <DownloadSimple size={14} /> Als Bild speichern
              </a>
              <button
                onClick={() => {
                  const text = `Bodenblick Wrapped ${year}: ${data.total_posts} Beiträge über Boden, Wetter und Bestäuber.`;
                  if (navigator.share) {
                    navigator.share({ title: `Bodenblick Wrapped ${year}`, text, url: window.location.href });
                  } else {
                    navigator.clipboard?.writeText(window.location.href);
                  }
                }}
                className="inline-flex items-center gap-2 border border-white/15 text-white px-5 py-3 text-sm font-medium uppercase tracking-widest hover:bg-white/10 hover:text-white transition-colors"
                data-testid="wrapped-share"
              >
                <ShareNetwork size={14} /> Teilen
              </button>
            </div>
            <p className="mt-5 text-[11px] uppercase tracking-[0.22em] font-mono text-white/60">
              1080 × 1920 · Bereit für Instagram Story
            </p>
          </div>
        </Scene>
      </div>
    </>
  );
}

function Scene({ children, bg = "#1A1A1A" }) {
  return (
    <section
      className="min-h-screen flex items-center justify-center relative overflow-hidden py-20"
      style={{ background: bg }}
    >
      {children}
    </section>
  );
}

function SceneLabel({ children }) {
  return (
    <motion.p
      initial={{ opacity: 0, y: 10 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
      className="text-[11px] uppercase tracking-[0.4em] font-mono opacity-70 inline-flex items-center gap-2"
    >
      <Sparkle size={11} weight="fill" /> {children}
    </motion.p>
  );
}

function ErrorWrap({ message, year }) {
  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center p-6">
      <div className="text-center max-w-md">
        <Calendar size={40} className="mx-auto opacity-60" />
        <h1 className="text-2xl font-bold mt-4">Wrapped {year} nicht verfügbar</h1>
        <p className="text-sm opacity-70 mt-2">{message}</p>
        <Link to="/" className="inline-block mt-6 text-[#FFC24B] hover:underline">← zur Startseite</Link>
      </div>
    </div>
  );
}
